import { inject } from '@angular/core';
import { Routes } from '@angular/router';

import {
  ConstantsGuard,
  KeycloakService,
  TranslationsGuard
} from '@ui/legacy-lib';
import {
  mailLandingAuthenticatedRoutes,
  mailLandingUnauthenticatedRoutes,
  nowStripeCheckoutRoutes
} from '@ui/tenant-pool/mail-landing/feature';
import { projectRegistrationLinkGuard } from '@ui/tenant-pool/mail-landing/util';
import { serviceCenterGuard } from '@ui/tenant-pool/service-center/util';
import {
  awaitingSubscriptionGuard,
  subscriptionLockRedirectGuard
} from '@ui/tenant-pool/shared/subscription-management/util';
import { PropertyNotFoundComponent } from '@ui/tenant-pool/shared/ui';

import { ApplicationCreationGuard } from './core/guards/application-creation.guard';
import { TenantTranslationsGuard } from './core/guards/tenant-translations.guard';
import { StoredRegistrationDataGuard } from './core/guards/stored-registration-data.guard';

import { AuthGuard } from './core/guards/auth.guard';

import { ROUTES as LandingRoutes } from './screens/landings/landings.routes';
import { MainPageNavigation } from './config/navigation.config';

export const ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/auth/login'
  },
  {
    path: 'tenant.app',
    redirectTo: '/auth/login'
  },
  {
    path: 'login',
    redirectTo: 'auth/login'
  },
  {
    path: 'register',
    redirectTo: 'auth/register'
  },
  {
    path: 'contact-register',
    redirectTo: 'auth/contact-register'
  },
  {
    path: 'guest-register',
    redirectTo: 'auth/guest-register'
  },
  {
    path: 'email-verification',
    redirectTo: 'auth/email-verification'
  },
  {
    path: 'confirm-new-email',
    redirectTo: 'auth/confirm-new-email'
  },
  {
    path: 'reset-password',
    redirectTo: 'auth/reset-password'
  },
  {
    path: 'impersonate',
    redirectTo: 'auth/impersonate'
  },
  {
    path: 'unlock',
    redirectTo: 'auth/unlock'
  },
  {
    path: 'project-registration/:token',
    canActivate: [projectRegistrationLinkGuard],
    children: []
  },
  {
    path: '',
    canActivate: [TranslationsGuard],
    children: [
      /**
       * LandingRoutes contain all the routes which are responsible for
       * landing operations, like apply or confirm/deny intent.
       * LandingModule is loaded eagerly.
       */
      ...LandingRoutes,
      ...mailLandingUnauthenticatedRoutes,
      {
        path: 'auth/logout',
        canActivate: [AuthGuard],
        children: mailLandingAuthenticatedRoutes
      },
      {
        path: MainPageNavigation.SCHUFA,
        title: 'navigation.schufa_page',
        loadChildren: () =>
          import('@ui/tenant-pool/schufa/feature').then(
            module => module.schufaRoutes
          )
      },
      {
        path: 'payment',
        title: 'navigation.payment_page',
        loadChildren: () =>
          import('./screens/payment/payment.routes').then(
            module => module.ROUTES
          )
      },
      {
        path: 'digitalContract',
        title: 'navigation.digital_contract_page',
        loadChildren: () =>
          import('./screens/digital-contract/digital-contract.routes').then(
            module => module.ROUTES
          )
      },
      {
        path: MainPageNavigation.TERMS_AND_CONDITIONS,
        loadComponent: () =>
          import('./screens/terms-and-conditions/terms-and-conditions.component').then(
            m => m.TermsAndConditionsComponent
          )
      },
      {
        path: MainPageNavigation.EMAIL_VERIFICATION_PENDING,
        loadComponent: () =>
          import('./screens/email-verification-pending/email-verification-pending.component').then(
            m => m.EmailVerificationPendingComponent
          )
      }
    ]
  },
  {
    path: '',
    canActivate: [TranslationsGuard],
    loadComponent: () =>
      import('./core/shell/unauthenticated-shell/unauthenticated-shell.component').then(
        m => m.UnauthenticatedShellComponent
      ),
    children: [
      {
        path: 'auth',
        loadChildren: () =>
          import('./auth/auth.routes').then(module => module.ROUTES)
      },
      {
        path: MainPageNavigation.NOW_STRIPE_CHECKOUT,
        canMatch: [() => !inject(KeycloakService).token],
        loadComponent: () =>
          import('@ui/tenant-pool/shared/subscription-management/feature').then(
            m => m.NowCheckoutPublicPageComponent
          ),
        data: { hideContentCentering: true },
        children: nowStripeCheckoutRoutes
      },
      {
        path: 'now-checkout',
        loadChildren: () =>
          import('@ui/tenant-pool/shared/subscription-management/feature').then(
            m => m.nowCheckoutPublicRoutes
          )
      }
    ]
  },
  // The routes are done via nested children, because the guards
  // need to be executed in order.
  // This is a technical debt issue and will hopefully be fixed via
  // https://immomio.atlassian.net/browse/ART-5391
  {
    path: '',
    loadComponent: () =>
      import('./core/shell/authenticated-shell/authenticated-shell.component').then(
        m => m.AuthenticatedShellComponent
      ),
    // AuthGuard assigns a parked registration token before it loads the user, so by
    // the time this level resolves the PS is already connected to the landlord customer
    // that StoredRegistrationDataGuard scopes the saved search profile to.
    canActivate: [
      AuthGuard,
      TenantTranslationsGuard,
      awaitingSubscriptionGuard,
      subscriptionLockRedirectGuard
    ],
    children: [
      {
        path: '',
        canActivate: [
          ConstantsGuard,
          ApplicationCreationGuard,
          StoredRegistrationDataGuard
        ],
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./screens/objects/objects.routes').then(
                module => module.ROUTES
              )
          },
          {
            path: 'messenger',
            data: {
              hideFooter: true,
              hideEmailBanner: true
            },
            loadChildren: () =>
              import('@ui/tenant-pool/messenger/feature').then(
                module => module.messengerRoutes
              )
          },
          {
            // The service centre lives in @ui/tenant-pool/service-center and
            // Schufa in @ui/tenant-pool/schufa; Schufa is its own domain because
            // the public `/schufa` page above renders the same wizard. It is
            // mounted here as a sibling of the service-centre routes. Every
            // entry is lazy, so none of this reaches the eager bundle.
            path: MainPageNavigation.SERVICE_CENTER,
            canActivate: [serviceCenterGuard],
            children: [
              {
                path: MainPageNavigation.SCHUFA,
                loadChildren: () =>
                  import('@ui/tenant-pool/schufa/feature').then(
                    module => module.serviceCenterSchufaRoutes
                  )
              },
              {
                path: '',
                loadChildren: () =>
                  import('@ui/tenant-pool/service-center/feature').then(
                    module => module.serviceCenterRoutes
                  )
              }
            ]
          },
          {
            path: MainPageNavigation.SUBSCRIPTION_LOCK,
            loadChildren: () =>
              import('@ui/tenant-pool/shared/subscription-management/feature').then(
                module => module.subscriptionLockRoutes
              )
          },
          {
            path: MainPageNavigation.NOW_STRIPE_CHECKOUT,
            children: nowStripeCheckoutRoutes
          },
          {
            path: 'profile',
            loadChildren: () =>
              import('@ui/tenant-pool/shared/profile/feature').then(
                module => module.profileRoutes
              )
          },
          {
            path: 'settings',
            loadChildren: () =>
              import('@ui/tenant-pool/settings/feature').then(
                module => module.settingsRoutes
              )
          },
          {
            path: 'rent-deposit/:matchId',
            loadChildren: () =>
              import('./screens/rent-deposit/rent-deposit.routes').then(
                module => module.ROUTES
              )
          },
          {
            path: `${MainPageNavigation.PROPERTIES}/${MainPageNavigation.PROPERTY_NOT_FOUND}`,
            component: PropertyNotFoundComponent,
            data: {
              useDefault: true
            }
          },
          {
            path: '**',
            redirectTo: MainPageNavigation.LANDLORDS
          }
        ]
      }
    ]
  }
];
